import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { Menu } from 'primereact/menu';
import * as generic from '../../../components/Generico';
import { useLazyParams } from '../../../hooks/useLazyParams';
import { TablaDinamica } from '../../../components/TablaDinamica';
import { AnexosComponent } from '../../../components/AnexosComponent';
import { useToken } from '../../../components/useToken';
import { FiltroMultiempresa } from '../../../components/FiltroMultiempresa';
import { OrigenService } from '../../../service/OrigenService';
import { Origen } from '../../../models/ficha/Origen';
import { DatosTransaccion } from './secciones/DatosTransaccion';
import { PagoBeneficiario } from './secciones/PagoBeneficiario';
import { Declaracion } from './secciones/Declaracion';
import { DialogInstrucciones } from '../../../components/ui/DialogInstrucciones';

const OrigenLicito = () => {
    const omodel = new Origen();
    const toast = useRef(null);
    const origenService = new OrigenService();
    const { getPerss, getRol } = useToken();
    // Estados globales del formulario
    const [origen, setOrigen] = useState(null);
    const [catalogos, setCatalogos] = useState(null);
    const [permss, setPermss] = useState(null);
    // Estados de navegación y UI
    const [dialogOrigen, setDialogOrigen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [siCliente, setSiCliente] = useState(false);
    const [urls, setUrls] = useState(null);
    const [dialogAnexos, setDialogAnexos] = useState(false);
    const [dialogInstruccion, setDialogInstruccion] = useState(false);
    const [soloLectura, setSoloLectura] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    const tablaEstado = useLazyParams({ modelo: omodel.model });
    const menuRef = useRef(null);
    const topRef = useRef(null);
    const [mostrarRegresarFlotante, setMostrarRegresarFlotante] = useState(false);

    useEffect(() => {
        setUrls({
            urlListado: 'ficha/origen',
            urlDownload: 'ficha/origen/descarga',
            urlUpload: 'ficha/origen/store',
            udelete: 'ficha/origen/eliminar-anexo',
            uenviar: 'ficha/origen/enviar',
            uaprobar: 'ficha/origen/aprobar',
        });
        const permisosToken = getPerss();
        const esClienteNotificado = permisosToken !== undefined;
        setSiCliente(esClienteNotificado);
        if (esClienteNotificado) { // FLUJO CLIENTE NOTIFICADO (autoservicio)
            setPermss(permisosToken);
            editFicha(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (getPerss() === undefined) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams]);

    // Efecto para observar el inicio del formulario
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setMostrarRegresarFlotante(!entry.isIntersecting);
            },
            { threshold: 0 }
        );
        const currentRef = topRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [dialogOrigen]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        const _lazyParams = { ...tablaEstado.lazyParams };
        delete _lazyParams.rtipo;
        delete _lazyParams.empresa;
        if (permss == null) {
            _lazyParams.rtipo = true;
        }
        if (empresa) {
            _lazyParams.empresa = empresa;
        }
        const rol = getRol();
        if (!rol) {
            toast.current.show({ severity: 'warn', summary: 'Rol no seleccionado', detail: 'Por favor selecciona un rol antes de continuar.', life: 3000 });
            tablaEstado.setLoading(false);
            return;
        } else {
            _lazyParams.rol = rol;
        }
        origenService.getOrigen({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
            if (res.data.perss) {
                setPermss(res.data.perss);
            }
            if (res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
        }).finally(() => {
            tablaEstado.setLoading(false);
        });
    }

    const iniciarNuevo = () => { // Los catálogos ya existen puesto que se cargan al abrir por primera vez
        setSoloLectura(false);
        setOrigen(null);
        setActiveIndex(0);
        setDialogOrigen(true);
        setDialogInstruccion(true);
    }

    const editFicha = (esNotificado = false, isReadOnly = false) => {
        setSoloLectura(isReadOnly);
        const idConsulta = esNotificado ? 0 : origen?.id;
        const necesitaCatalogos = catalogos === null;
        origenService.consultarOrigen(idConsulta, null, { necesitaCatalogos: necesitaCatalogos }).then(res => {
            // Asumiendo que el backend devolverá la misma estructura: res.data.catalogos y res.data.origen
            if (necesitaCatalogos && res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
            if (!res.data.origen) {
                iniciarNuevo();
                return;
            }
            setDialogOrigen(true);
            setActiveIndex(0);
            setDialogInstruccion(true);

            nuevoOrigen({ id: res.data.origen.id, nemonico: res.data.origen.nemonico });
        });
    }

    const confirmDelete = () => {
        confirmDialog({
            message: '¿Está seguro de eliminar el formulario?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteOrigen()
        });
    }

    const deleteOrigen = () => {
        origenService.eliminarOrigen(origen?.id).then(res => {
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => {
            generic.funcDeleteError(error, toast);
        });
    }

    // FUNCIÓN VITAL PARA MANTENER SINCRONIZADAS LAS SECCIONES
    const nuevoOrigen = (nuevo) => {
        if (!nuevo) {
            setOrigen(null);
            return;
        }
        setOrigen(prev => ({ ...prev, ...nuevo }));
    }

    // CONTROLES DE NAVEGACIÓN (Siguiente / Anterior)
    const handleNext = () => {
        if (activeIndex < 2) setActiveIndex(activeIndex + 1);
    };

    const handlePrev = () => {
        if (activeIndex > 0) setActiveIndex(activeIndex - 1);
    };

    const regresar = () => {
        setActiveIndex(0);
        setDialogOrigen(false);
        setSoloLectura(false);
        if (!siCliente) loadLazyData();
    }

    const reiniciar = () => {
        if (!siCliente) loadLazyData();
        setDialogOrigen(false);
        setDialogAnexos(false);
    }

    const itemsMenu = () => {
        const items = [];
        if (permss?.consultar) {
            items.push({ label: 'Ver', icon: 'pi pi-eye', command: () => { editFicha(false, true); } });
        }
        if (permss?.actualizar && (!origen?.nemonico || origen?.nemonico !== 'FINALIZADO')) {
            items.push({ label: 'Editar', icon: 'pi pi-pencil', command: () => { editFicha(); } });
        }
        if (permss?.consultar) {
            items.push({ label: 'Anexos formulario', icon: 'pi pi-folder-open', command: () => { setDialogAnexos(true); } });
        }
        if (permss?.eliminar && (!origen?.nemonico || origen.nemonico !== 'FINALIZADO')) {
            items.push({ label: 'Eliminar', icon: 'pi pi-trash', command: () => { confirmDelete(); } });
        }
        return [{ label: 'Opciones', items: items }];
    }

    const accionesTemplate = (rowData) => {
        return (
            <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu" aria-haspopup
                onClick={(event) => { setOrigen(rowData); menuRef.current.toggle(event); }} />
        );
    };

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '6rem', textAlign: 'center' }} />
    );

    const panelHeaderTemplate = () => (
        <div className="p-panel-header flex justify-content-between align-items-center flex-wrap gap-2 w-full">
            <div>
                <h3 className="m-0 text-xl font-bold p-text-secondary flex align-items-center">
                    <i className="pi pi-file-edit mr-2"></i>
                    <span className="mr-2">{soloLectura ? 'VER' : (origen?.id ? 'EDITAR' : 'NUEVO')} FORMULARIO DE ORIGEN LÍCITO DE RECURSOS</span>
                    <Button type="button" icon="pi pi-question-circle" className="p-button-rounded p-button-text p-button-sm p-0 m-0 text-white hover:text-blue-200 transition-colors transition-duration-200"
                        style={{ width: '2rem', height: '2rem' }} onClick={() => setDialogInstruccion(true)} tooltip="Ver instrucciones" tooltipOptions={{ position: 'right' }} />
                </h3>
            </div>
            {!siCliente && (
                <Button tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} icon="pi pi-arrow-left" onClick={regresar} />
            )}
        </div>
    );

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu" />
            {!dialogOrigen && !siCliente && (
                <div className="fadein animation-duration-300">
                    {getRol() && getRol()?.ideg && <FiltroMultiempresa loadLazyData={loadLazyData} setEmpresa={setEmpresa} />}
                    <Toolbar className="mb-4 shadow-1 border-round" start={(getRol() && getRol()?.ideg) ? null : generic.leftToolbarTemplate(iniciarNuevo, 'Nuevo', permss)}
                        end={generic.rightToolbarTemplate(tablaEstado.data, omodel, 'Reporte_origen_licito', 'LISTADO ORIGEN LICITO')} />
                    <Card style={{ maxWidth: '100%', margin: 'auto' }}>
                        <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="id" acciones={ColumnaAcciones} />
                    </Card>
                </div>
            )}
            {dialogOrigen &&
                <div className="fadein animation-duration-300">
                    <Panel headerTemplate={panelHeaderTemplate} className="panel-ficha shadow-2">
                        <div ref={topRef} style={{ position: 'absolute', top: '0', left: '0', width: '1px', height: '80px', pointerEvents: 'none', visibility: 'hidden' }} />
                        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="form-tabview">
                            <TabPanel header="Sección 1" leftIcon="pi pi-id-card mr-2">
                                <DatosTransaccion toast={toast} id={origen?.id} permss={permss} catalogos={catalogos}
                                    next={handleNext} nuevoOrigen={nuevoOrigen} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 2" leftIcon="pi pi-wallet mr-2" disabled={!origen?.id}>
                                <PagoBeneficiario toast={toast} id={origen?.id} permss={permss} catalogos={catalogos}
                                    handlePrev={handlePrev} next={handleNext} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 3" leftIcon="pi pi-file-edit mr-2" disabled={!origen?.id}>
                                <Declaracion toast={toast} id={origen?.id} permss={permss} catalogos={catalogos}
                                    handlePrev={handlePrev} mostrarAnexos={() => setDialogAnexos(true)} soloLectura={soloLectura} />
                            </TabPanel>
                        </TabView>
                    </Panel>

                    {mostrarRegresarFlotante && !siCliente && (
                        <div className="fadein scalein" style={{ position: 'fixed', top: '65px', right: '30px', zIndex: 999 }}>
                            <Button icon="pi pi-arrow-left" onClick={regresar} rounded className="p-button-primary shadow-4" style={{ width: '45px', height: '45px' }} tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} />
                        </div>
                    )}
                </div>
            }

            <DialogInstrucciones visible={dialogInstruccion} onHide={() => setDialogInstruccion(false)} title={omodel.titulo} items={omodel.pasosInstrucciones} />
            <Dialog visible={dialogAnexos} header="Gestión de Anexos" modal className="p-fluid modal-ssize" onHide={() => setDialogAnexos(false)} style={{ width: '800px' }}>
                <AnexosComponent tipo="origen" urls={urls} idp={origen?.id} toast={toast} permss={permss} setDialogSubir={setDialogAnexos} reiniciar={reiniciar} estado={origen?.nemonico} soloLectura={soloLectura} />
            </Dialog>
        </>
    )
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(OrigenLicito, comparisonFn);
